import { Fragment, SetStateAction, Dispatch } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon, ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import Image from "next/image";
import RiskBadge from "./riskBadge";
import Countries from "./countries";

export default function MoreInfo({
	animal,
	open,
	setOpen,
	setCountry,
}: {
	animal: Animal;
	open: boolean;
	setOpen: Dispatch<SetStateAction<boolean>>;
	setCountry?: Dispatch<SetStateAction<string>>;
}) {
	if (!animal) return <></>;
	return (
		<Transition.Root show={open} as={Fragment} key={animal.name}>
			<Dialog as="div" className="relative z-10" onClose={setOpen}>
				<Transition.Child
					as={Fragment}
					enter="ease-in-out duration-500"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in-out duration-500"
					leaveFrom="opacity-100"
					leaveTo="opacity-0">
					<div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
				</Transition.Child>

				<div className="fixed inset-0 overflow-hidden">
					<div className="absolute inset-0 overflow-hidden">
						<div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
							<Transition.Child
								as={Fragment}
								enter="transform transition ease-in-out duration-500 sm:duration-700"
								enterFrom="translate-x-full"
								enterTo="translate-x-0"
								leave="transform transition ease-in-out duration-500 sm:duration-700"
								leaveFrom="translate-x-0"
								leaveTo="translate-x-full">
								<Dialog.Panel className="pointer-events-auto relative w-96">
									<Transition.Child
										as={Fragment}
										enter="ease-in-out duration-500"
										enterFrom="opacity-0"
										enterTo="opacity-100"
										leave="ease-in-out duration-500"
										leaveFrom="opacity-100"
										leaveTo="opacity-0">
										<div className="absolute top-0 left-0 -ml-8 flex pt-4 pr-2 sm:-ml-10 sm:pr-4">
											<button
												type="button"
												className="rounded-md text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
												onClick={() => setOpen(false)}>
												<span className="sr-only">Close panel</span>
												<XMarkIcon className="h-6 w-6" aria-hidden="true" />
											</button>
										</div>
									</Transition.Child>
									<div className="h-full overflow-y-auto bg-white p-8">
										<div className="space-y-6 pb-16">
											<div>
												<div className="aspect-w-10 aspect-h-7 block w-full overflow-hidden rounded-lg">
													<Image src={animal.image} width={400} height={300} alt="Animal Picture" className="object-cover" />
												</div>
												<div className="mt-4 flex items-start justify-between">
													<div>
														<h2 className="text-lg font-medium text-gray-900">
															<span className="sr-only">Details for </span>
															{animal.name}
														</h2>
													</div>
													<button
														type="button"
														className="ml-4 flex h-8 w-8 items-center justify-center rounded-full bg-white text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cary-400"
														onClick={() => {
															navigator.clipboard.writeText(`${window.location.host}/data?animal=${encodeURIComponent(animal.name)}`);
														}}>
														<ClipboardDocumentIcon className="h-6 w-6" aria-hidden="true" />
														<span className="sr-only">Copy</span>
													</button>
												</div>
												<p className="max-h-44 overflow-auto text-sm font-medium text-gray-500">
													<Countries countries={animal.countries} setCountry={setCountry} />
												</p>
											</div>
											<div>
												<h3 className="font-medium text-gray-900">Information</h3>
												<dl className="mt-2 divide-y divide-gray-200 border-t border-b border-gray-200">
													<div className="flex justify-between py-3 text-sm font-medium">
														<dt className="text-gray-500">Common Name</dt>
														<dd className="text-gray-900">{animal.commonName}</dd>
													</div>
													<div className="flex justify-between py-3 text-sm font-medium">
														<dt className="text-gray-500">Risk Level</dt>
														<dd className="text-gray-900">
															<RiskBadge level={animal.level} />
														</dd>
													</div>
													<div className="flex justify-between py-3 text-sm font-medium">
														<dt className="text-gray-500">Risk Score</dt>
														<dd className="text-gray-900">{animal.riskScore}</dd>
													</div>
													{animal.validation && (
														<>
															<div className="flex justify-between py-3 text-sm font-medium">
																<dt className="text-gray-500">Validation</dt>
																<dd className="text-gray-900">
																	<a
																		href={animal.validationLink}
																		target="_BLANK"
																		rel="noreferrer"
																		className="ml-6 rounded-md bg-white text-sm font-medium text-cary-400 hover:text-cary-300 focus:outline-none focus:ring-2 focus:ring-cary-300 focus:ring-offset-2">
																		{animal.validation}
																	</a>
																</dd>
															</div>
														</>
													)}
													<div className="justify-between py-3 text-sm font-medium md:flex">
														<dt className="whitespace-nowrap text-gray-500">Known Habitats</dt>
														<dd className="text-gray-900 md:text-right">{animal.habitats.join(", ")}</dd>
													</div>
													<div className="flex justify-between py-3 text-sm font-medium">
														<dt className="text-gray-500">Category</dt>
														<dd className="text-gray-900">{animal.category}</dd>
													</div>
													<div className="flex justify-between py-3 text-sm font-medium">
														<dt className="text-gray-500">Order</dt>
														<dd className="text-gray-900">{animal.order}</dd>
													</div>
													<div className="flex justify-between py-3 text-sm font-medium">
														<dt className="text-gray-500">Family</dt>
														<dd className="text-gray-900">{animal.family}</dd>
													</div>
												</dl>
											</div>
										</div>
									</div>
								</Dialog.Panel>
							</Transition.Child>
						</div>
					</div>
				</div>
			</Dialog>
		</Transition.Root>
	);
}
